import { addMonths, endOfDay, endOfMonth, endOfWeek, endOfYear, isValid } from 'date-fns';
import { TERMS_AGGREGATION_MISSING_VALUE_NAME } from 'app/domains/insights/constants';
import { DocumentMetadataPropertyTypeEnum, SearchAggregationDateInterval, SearchAggregationType, } from 'shared/domains/apollo/generated/types';
import { getSelectedField, getSelectedInput } from './getSelectedField';
import { getMetadataProperty } from '../../utils/metadataProperties';
/**
 * Search fields with no metadata property behind them. These are our non-metadata fields.
 */
const STATIC_FIELD_FILTERS = {
    categoryId: 'in',
    createdByUserId: 'in',
    entityType: 'in',
    isPlGenerated: 'boolean',
    marketCode: 'in',
    metadataDocumentEsigningStatus: 'in',
    metadataDocumentIsSharedExternally: 'boolean',
    metadataDocumentStatus: 'in',
    metadataDocumentType: 'in',
    metadataTaskIsPublic: 'boolean',
};
/** Multi-value fields that take `hasSome` rather than `in`. */
const STATIC_ARRAY_FIELDS = [
    'metadataDocumentParties',
    'metadataProjectCounterparties',
    'metadataProjectOurParties',
];
function isArrayField(field) {
    // Custom multi-value properties live in the `...KeywordArrayNN` / `...TextArrayNN` slots.
    return /CustomProperty(Keyword|Text)Array\d+$/.test(field) || STATIC_ARRAY_FIELDS.includes(field);
}
function getDateFilter(field, value, aggregation) {
    var _a;
    if (!isValid(new Date(value))) {
        return {};
    }
    const { interval } = getSelectedInput(aggregation);
    const intervalFunctions = {
        [SearchAggregationDateInterval.Day]: () => endOfDay(new Date(value)),
        [SearchAggregationDateInterval.Month]: () => endOfMonth(new Date(value)),
        [SearchAggregationDateInterval.Week]: () => endOfWeek(new Date(value)),
        [SearchAggregationDateInterval.Year]: () => endOfYear(new Date(value)),
        [SearchAggregationDateInterval.Quarter]: () => endOfMonth(addMonths(new Date(value), 3)),
    };
    const endOfInterval = (_a = intervalFunctions[interval]) === null || _a === void 0 ? void 0 : _a.call(intervalFunctions);
    if (!endOfInterval) {
        return {};
    }
    return { [field]: { gte: value, lte: endOfInterval.toISOString() } };
}
function applyFilterKind(kind, field, value, aggregation) {
    switch (kind) {
        case 'boolean':
            return { [field]: { eq: value === 'true' } };
        case 'date':
            return getDateFilter(field, `${value}`, aggregation);
        case 'hasSome':
            return { [field]: { hasSome: [value] } };
        case 'in':
            return { [field]: { in: [value] } };
        case 'eq':
        default:
            return { [field]: { eq: value } };
    }
}
function getFilterKindForProperty(property) {
    switch (property.type) {
        case DocumentMetadataPropertyTypeEnum.Boolean:
            return 'boolean';
        case DocumentMetadataPropertyTypeEnum.List:
        case DocumentMetadataPropertyTypeEnum.Reference:
            return 'in';
        case DocumentMetadataPropertyTypeEnum.Date:
        case DocumentMetadataPropertyTypeEnum.Datetime:
        case DocumentMetadataPropertyTypeEnum.Number:
        case DocumentMetadataPropertyTypeEnum.SimpleText:
        case DocumentMetadataPropertyTypeEnum.MultiLineText:
        default:
            return 'eq';
    }
}
function getFilterKind(field, aggregation) {
    const isDateAggregation = aggregation.type === SearchAggregationType.DateHistogram || aggregation.type === SearchAggregationType.DateRange;
    if (isDateAggregation) {
        return 'date';
    }
    if (isArrayField(field)) {
        return 'hasSome';
    }
    const property = getMetadataProperty(field);
    if (property) {
        return getFilterKindForProperty(property);
    }
    return STATIC_FIELD_FILTERS[field] || 'eq';
}
/**
 * Convert a bucket and aggregation to a search filter
 */
export function bucketAggregationToFilter(bucket, aggregation) {
    const { name } = bucket || {};
    const field = getSelectedField(aggregation);
    if (!bucket || !aggregation || !field) {
        return {};
    }
    if (name === TERMS_AGGREGATION_MISSING_VALUE_NAME) {
        return { [field]: { isNull: true } };
    }
    if (!name) {
        return {};
    }
    return applyFilterKind(getFilterKind(field, aggregation), field, name, aggregation);
}
